import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';

//* Context
import { withUIContext } from '@/context';

//* Components
import Icon from '../Icon';

//* Styles
import VideoStyle from './style';

const Video = forwardRef(({ isIcon = true, winWidth, double, ended, ...props }, ref) => {
	//! Ref
	const videoRef = useRef();

	//! Imperative Handle
	useImperativeHandle(ref, () => videoRef.current, []);

	//! Mobile Desktop
	const doubleSrc = double ? (winWidth >= 1280 ? double?.[0]?.src : double?.[1]?.src) : null;

	//! States
	const [isPlaying, setIsPlaying] = useState(false);
	const [showControls, setShowControls] = useState(true);

	//! Auto Play on Scroll
	useEffect(() => {
		if (!videoRef.current) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				const video = videoRef.current?.querySelector('video');

				if (video) {
					if (entry.isIntersecting) {
						const playPromise = video.play();

						if (playPromise !== undefined) {
							playPromise.catch(() => console.warn('Autoplay prevented.'));
						}

						setIsPlaying(true);
						setShowControls(false);
					} else {
						video.pause();

						setIsPlaying(false);
						setShowControls(true);
					}
				}
			},
			{ threshold: 1 }
		);

		observer.observe(videoRef.current);

		return () => {
			observer.disconnect();
		};
	}, []);

	const togglePlay = () => {
		const video = videoRef.current?.querySelector('video');

		if (!video) return;

		if (video.paused || video.ended) {
			video.play();

			setIsPlaying(true);
			setShowControls(false);
		} else {
			video.pause();

			setIsPlaying(false);
			setShowControls(true);
		}
	};

	const handleMouseEnter = () => {
		setShowControls(true);
	};

	const handleMouseLeave = () => {
		isPlaying && setShowControls(false);
	};

	const handleVideoEnd = () => {
		const video = videoRef.current?.querySelector('video');

		if (video) {
			video.currentTime = 0;
			video.pause();

			setShowControls(true);
			setIsPlaying(false);
		}
	};

	return (
		<VideoStyle
			ref={videoRef}
			onClick={togglePlay}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			className={`video-cont ${isIcon && showControls ? 'video-background' : ''} ${props.className || ''}`}>
			<video
				muted
				playsInline
				autoPlay={false}
				loop={props.loop}
				preload='metadata'
				controls={props.controls}
				onClick={props.handleClick}
				onEnded={ended && handleVideoEnd}>
				<source
					src={`${props?.src || doubleSrc}#t=0.1`}
					type='video/mp4'
				/>
				<source
					src={`${props?.src || doubleSrc}#t=0.1`}
					type='video/webm'
				/>
			</video>

			{isIcon && showControls ? <div className='video-icon'>{isPlaying ? <Icon name='pause' /> : <Icon name='play' />}</div> : null}
		</VideoStyle>
	);
});

Video.displayName = 'Video';

export default withUIContext(Video, ['winWidth']);
